import React, { useState, useEffect, useMemo } from 'react';
import { useWBL } from '../../context/WBLContext';
import { HeroBanner } from '../Common/HeroBanner';
import { StudentDashboard } from './StudentDashboard';
import { 
  ShieldAlert, 
  User, 
  CheckCircle2, 
  Loader2, 
  Sparkles, 
  Key, 
  Plus, 
  X, 
  GraduationCap,
  Shield,
  Lock
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { UserProfile } from '../../types';

export const ParentDashboard: React.FC = () => {
  const {
    activeUser,
    users,
    userLinks,
    verifyStudentTicket,
    linkStudent,
    switchActiveChild,
    activeTenant
  } = useWBL();

  const [linkDob, setLinkDob] = useState('');
  const [linkCode, setLinkCode] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);

  // Map of loaded child profiles
  const [loadedChildren, setLoadedChildren] = useState<Record<string, UserProfile>>({});
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);

  // Collect all unique child IDs linked to this parent in stable physical order
  const parentChildrenIds = useMemo(() => {
    const ids = new Set<string>();
    if (activeUser?.childrenIds && Array.isArray(activeUser.childrenIds)) {
      activeUser.childrenIds.forEach(id => ids.add(id));
    }
    if (activeUser?.uid && Array.isArray(userLinks)) {
      userLinks
        .filter(l => l.parentId === activeUser.uid && l.studentId)
        .forEach(l => ids.add(l.studentId));
    }
    if (activeUser?.childId) ids.add(activeUser.childId);

    const idsArray = Array.from(ids);
    return idsArray.sort((a, b) => {
      const nameA = users.find(u => u.uid === a)?.name || userLinks?.find(l => l.studentId === a)?.studentName || a;
      const nameB = users.find(u => u.uid === b)?.name || userLinks?.find(l => l.studentId === b)?.studentName || b;
      return nameA.localeCompare(nameB);
    });
  }, [activeUser, userLinks, users]);

  const parentChildrenIdsKey = parentChildrenIds.join(',');

  // Selected child ID (defaults to locally selected or activeUser.childId or first in list)
  const activeChildId = selectedChildId || activeUser?.childId || (parentChildrenIds.length > 0 ? parentChildrenIds[0] : null);

  // Sync child profiles
  useEffect(() => {
    if (!parentChildrenIdsKey) return;

    let isMounted = true;
    const fetchMissingProfiles = async () => {
      // Determine which profiles are actually missing from state
      const missingIds = parentChildrenIds.filter(cId => !loadedChildren[cId]);
      if (missingIds.length === 0) return;

      setIsLoadingProfiles(true);
      const newProfilesMap: Record<string, UserProfile> = {};

      for (const cId of missingIds) {
        const fromState = users.find(u => u.uid === cId);
        if (fromState) {
          newProfilesMap[cId] = fromState;
        } else {
          try {
            const snap = await getDoc(doc(db, 'users', cId));
            if (snap.exists()) {
              newProfilesMap[cId] = { uid: snap.id, ...snap.data() } as UserProfile;
            }
          } catch (err) {
            console.warn(`Failed to fetch student profile for ${cId}:`, err);
          }
        }
      }

      if (isMounted && Object.keys(newProfilesMap).length > 0) {
        setLoadedChildren(prev => ({ ...prev, ...newProfilesMap }));
        setIsLoadingProfiles(false);
      }
    };

    fetchMissingProfiles();
    return () => {
      isMounted = false;
    };
  }, [parentChildrenIdsKey, users]);

  // Resilient student profile resolution (always guarantees a valid UserProfile structure)
  const activeChildProfile = useMemo(() => {
    if (!activeChildId) return null;
    if (loadedChildren[activeChildId]) return loadedChildren[activeChildId];
    const fromUsers = users.find(u => u.uid === activeChildId);
    if (fromUsers) return fromUsers;

    const linked = userLinks?.find(l => l.studentId === activeChildId);
    return {
      uid: activeChildId,
      name: linked?.studentName || 'Student',
      email: '',
      role: 'student' as const,
      tenantId: linked?.tenantId || activeUser?.tenantId || activeTenant?.id || 'global',
      school: activeTenant?.name || 'WBL Program',
      district: '',
      ferpaConsent: activeUser?.ferpaConsent || true,
      advisorVerified: true
    } as UserProfile;
  }, [activeChildId, loadedChildren, users, userLinks, activeUser, activeTenant]);

  const handleSelectChild = async (studentUid: string) => {
    setSelectedChildId(studentUid);
    if (switchActiveChild) {
      try {
        await switchActiveChild(studentUid);
      } catch (err) {
        console.warn("Switch child sync:", err);
      }
    }
  };

  const handleManualLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLinkError(null);
    setIsLinking(true);
    try {
      if (!linkDob || !linkCode) throw new Error("Please provide birth date and access code.");
      
      const studentUid = await verifyStudentTicket(linkDob, linkCode);
      if (!studentUid) {
        throw new Error("Student not found. Please verify the Birth Date and Access Code provided by your student.");
      }

      const studentName = users.find(u => u.uid === studentUid)?.name || "Linked Student";
      await linkStudent(studentUid, studentName);
      setSelectedChildId(studentUid);
      
      // Also notify backend API
      try {
        await fetch('/api/link-student-to-parent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            parentUid: activeUser?.uid,
            parentEmail: activeUser?.email,
            studentUid,
            studentName
          })
        });
      } catch (apiErr) {
        console.warn("Backend link notification note:", apiErr);
      }

      setLinkDob('');
      setLinkCode('');
      setShowAddStudentModal(false);
    } catch (err: any) {
      setLinkError(err.message);
    } finally {
      setIsLinking(false);
    }
  };

  // If no children linked at all, show clean onboarding link card
  if (parentChildrenIds.length === 0 && !activeChildId) {
    return (
      <div className="space-y-6 pb-12">
        <HeroBanner 
          title="DASHBOARD"
          subtitle={`Role: PARENT | Perspective: ${activeTenant?.name || 'TRAINING ENVIRONMENT'}`}
          pageKey="dashboard"
        >
          <div className="absolute top-6 right-0 z-10 flex items-center space-x-3">
             <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center border border-white/30 text-white">
                <User className="w-5 h-5" />
             </div>
          </div>
        </HeroBanner>

        <div className="w-[95%] max-w-xl mx-auto space-y-6">
          <div className="bg-white border-2 border-dashed border-slate-200 rounded-[2.5rem] p-10 text-center shadow-sm">
            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-slate-100">
              <ShieldAlert style={{ color: activeTenant?.primaryColor || '#0094c8' }} className="w-8 h-8 opacity-40" />
            </div>
            <h3 className="font-black text-slate-900 text-xl tracking-tight uppercase">Account Synchronization Required</h3>
            <p className="text-xs text-slate-500 mt-3 font-bold uppercase tracking-widest leading-relaxed">
              Your guardian account is active but not yet linked to a student profile. Enter your student's unique access credentials below to establish a secure real-time link.
            </p>
          </div>

          <form onSubmit={handleManualLink} className="bg-white border border-slate-200 rounded-[2rem] p-8 shadow-xl space-y-6">
            <div className="flex items-center space-x-3 mb-2">
                <div style={{ backgroundColor: `${activeTenant?.primaryColor || '#0094c8'}1a`, color: activeTenant?.primaryColor || '#0094c8' }} className="p-2.5 rounded-xl">
                    <Key className="w-5 h-5" />
                </div>
                <h4 className="font-black text-xs text-slate-900 uppercase tracking-widest transition-all">Identity Verification</h4>
            </div>

            <div className="grid grid-cols-1 gap-6">
                <div className="space-y-1.5">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Student Date of Birth</label>
                    <input 
                        type="date"
                        className="w-full px-5 py-3.5 bg-slate-50 focus:bg-white border-0 focus:ring-2 rounded-xl font-bold text-slate-900 transition-all outline-none"
                        value={linkDob}
                        onChange={e => setLinkDob(e.target.value)}
                        onFocus={(e) => e.target.style.boxShadow = `0 0 0 2px ${activeTenant?.primaryColor || '#0094c8'}`}
                        onBlur={(e) => e.target.style.boxShadow = 'none'}
                    />
                </div>
                <div className="space-y-1.5">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Student Link Code</label>
                    <input 
                        type="text"
                        placeholder="e.g. AB1234"
                        className="w-full px-5 py-3.5 bg-slate-50 focus:bg-white border-0 focus:ring-2 rounded-xl font-bold text-slate-900 transition-all outline-none"
                        value={linkCode}
                        onChange={e => setLinkCode(e.target.value.toUpperCase())}
                        onFocus={(e) => e.target.style.boxShadow = `0 0 0 2px ${activeTenant?.primaryColor || '#0094c8'}`}
                        onBlur={(e) => e.target.style.boxShadow = 'none'}
                    />
                </div>
            </div>

            {linkError && (
              <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-center space-x-3 text-red-600 animate-shake">
                <ShieldAlert className="w-4 h-4 shrink-0" />
                <p className="text-[10px] font-bold uppercase tracking-tight leading-tight">{linkError}</p>
              </div>
            )}

            <button 
              type="submit"
              disabled={isLinking}
              style={{ backgroundColor: activeTenant?.primaryColor || '#0094c8', boxShadow: `0 10px 15px -3px ${activeTenant?.primaryColor || '#0094c8'}33` }}
              className="w-full py-4 text-white rounded-2xl text-[11px] font-black uppercase tracking-[0.2em] transition-all hover:opacity-90 active:scale-[0.98] flex items-center justify-center space-x-3 disabled:opacity-50 cursor-pointer"
            >
              {isLinking ? (
                <>
                   <Loader2 className="w-5 h-5 animate-spin" />
                   <span>Authenticating Link...</span>
                </>
              ) : (
                <>
                   <Sparkles className="w-4 h-4" />
                   <span>ESTABLISH SECURE CONNECTION</span>
                </>
              )}
            </button>
          </form>

          <p className="text-center text-[10px] text-slate-400 font-bold uppercase tracking-widest flex items-center justify-center gap-2">
            <CheckCircle2 className="w-3 h-3 text-slate-300" />
            Verified link provides real-time access to progress tracking
          </p>
        </div>
      </div>
    );
  }

  // Concise Family Switcher Component to embed directly inside the Student Profile Card
  const familySwitcherNode = (
    <div className="flex items-center flex-wrap gap-1.5">
      {parentChildrenIds.map((cId) => {
        const child = loadedChildren[cId] || users.find(u => u.uid === cId);
        const linked = userLinks?.find(l => l.studentId === cId);
        const isSelected = cId === activeChildId;
        const displayName = child?.name || linked?.studentName || `Student (${cId.slice(0, 6)})`;
        const schoolName = child?.school || child?.region || activeTenant?.name || 'WBL';

        return (
          <button
            key={cId}
            type="button"
            onClick={() => handleSelectChild(cId)}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center space-x-1.5 cursor-pointer border ${
              isSelected
                ? 'bg-slate-900 text-white border-slate-900 shadow-xs'
                : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border-slate-200'
            }`}
          >
            <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-black ${
              isSelected ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'
            }`}>
              {displayName.charAt(0)}
            </div>
            <span className="font-bold">{displayName}</span>
            <span className={`text-[9px] font-normal ${isSelected ? 'text-slate-300' : 'text-slate-400'}`}>
              ({schoolName})
            </span>
          </button>
        );
      })}

      <button
        type="button"
        onClick={() => setShowAddStudentModal(true)}
        className="px-2.5 py-1.5 rounded-xl text-xs font-bold text-slate-700 hover:text-slate-900 bg-white hover:bg-slate-50 border border-dashed border-slate-300 hover:border-slate-400 transition-all flex items-center space-x-1 cursor-pointer"
        title="Link another student"
      >
        <Plus className="w-3.5 h-3.5 text-slate-500" />
        <span className="text-[11px]">Add Student</span>
      </button>
    </div>
  );

  return (
    <div className="relative min-h-screen bg-slate-50/50 pb-20">
      {/* Modal: Link Another Student */}
      {showAddStudentModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl border border-slate-200 max-w-md w-full p-6 shadow-2xl space-y-5 animate-in fade-in zoom-in duration-150">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2.5">
                <div className="p-2 bg-[#0094c8]/10 text-[#0094c8] rounded-xl">
                  <GraduationCap className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">Link Another Student</h3>
                  <p className="text-[11px] text-slate-500 font-medium">Add a student to your guardian dashboard</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowAddStudentModal(false);
                  setLinkError(null);
                }}
                className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleManualLink} className="space-y-4">
              <div className="space-y-1">
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest">Student Date of Birth</label>
                <input 
                  type="date"
                  required
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-900 text-xs outline-none focus:ring-2 focus:ring-[#0094c8]"
                  value={linkDob}
                  onChange={e => setLinkDob(e.target.value)}
                />
              </div>

              <div className="space-y-1">
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest">Student Link Access Code</label>
                <input 
                  type="text"
                  required
                  placeholder="e.g. AB1234"
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-900 text-xs outline-none focus:ring-2 focus:ring-[#0094c8] uppercase"
                  value={linkCode}
                  onChange={e => setLinkCode(e.target.value.toUpperCase())}
                />
                <p className="text-[10px] text-slate-400">Your student can generate this code inside their Student Portal.</p>
              </div>

              {linkError && (
                <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-xs font-medium flex items-center space-x-2">
                  <ShieldAlert className="w-4 h-4 shrink-0" />
                  <span>{linkError}</span>
                </div>
              )}

              <div className="flex items-center justify-end space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddStudentModal(false);
                    setLinkError(null);
                  }}
                  className="px-4 py-2.5 text-slate-600 hover:text-slate-900 text-xs font-bold uppercase tracking-wider rounded-xl hover:bg-slate-100 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLinking}
                  className="px-5 py-2.5 bg-[#0094c8] hover:bg-[#0082b0] text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-md cursor-pointer flex items-center space-x-2 disabled:opacity-50"
                >
                  {isLinking ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Linking...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Link Student</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main Student Dashboard View or 18yo FERPA Transition Notice */}
      {activeChildProfile && (() => {
        const isRevoked = activeChildProfile.allowParentOversightAt18 === false || activeChildProfile.parentAccessRevokedAt18 === true;
        
        if (isRevoked) {
          return (
            <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
              <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-2xs">
                {familySwitcherNode}
              </div>

              <div className="bg-white border border-slate-200/90 rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-xl max-w-2xl mx-auto my-6 animate-fade-in">
                <div className="w-16 h-16 bg-amber-100 text-amber-800 rounded-2xl flex items-center justify-center mx-auto shadow-2xs border border-amber-200">
                  <Shield className="w-8 h-8" />
                </div>

                <div className="space-y-2">
                  <span className="px-3.5 py-1 bg-amber-100/80 text-amber-900 text-[10px] font-black uppercase tracking-widest rounded-full font-mono">
                    FERPA Statutory Rights Transfer • Age 18
                  </span>
                  <h2 className="text-xl sm:text-2xl font-black text-slate-900 uppercase tracking-tight">
                    {activeChildProfile.name} Has Reached Age 18
                  </h2>
                </div>

                <div className="p-6 bg-slate-50 border border-slate-200/80 rounded-2xl text-left space-y-3">
                  <p className="text-xs text-slate-700 leading-relaxed font-medium">
                    Under federal FERPA legislation (34 CFR § 99.5), educational privacy rights legally transfer from parent/guardian oversight directly to the student upon reaching their 18th birthday.
                  </p>
                  <p className="text-xs text-slate-700 leading-relaxed font-medium">
                    <strong className="text-slate-900">{activeChildProfile.name}</strong> has exercised their statutory right under FERPA to self-manage their Work-Based Learning account. Parental dashboard access for this student profile has been disabled by the student.
                  </p>
                </div>

                <p className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">
                  If you believe this is in error or require official school assistance, please contact the student's pathway advisor.
                </p>
              </div>
            </div>
          );
        }

        return (
          <div className="pointer-events-auto">
            <StudentDashboard 
              overrideUser={activeChildProfile} 
              isReadOnly={true} 
              familySwitcher={familySwitcherNode} 
            />
          </div>
        );
      })()}
    </div>
  );
};
